import React, { useEffect, useState } from 'react';
import { CustomModal } from '../CustomModal';
import { Btn, InputField } from '../ui';
import type { CompanyUnitOfMeasureRecord } from '../../graphql/companyUnitOfMeasureQueries';

interface UnitOfMeasureModalProps {
    visible: boolean;
    existing: CompanyUnitOfMeasureRecord[];
    editing: CompanyUnitOfMeasureRecord | null;
    isSaving: boolean;
    error: string | null;
    onDismiss: () => void;
    onSubmit: (name: string) => void;
}

const UnitOfMeasureModal: React.FC<UnitOfMeasureModalProps> = ({
    visible,
    existing,
    editing,
    isSaving,
    error,
    onDismiss,
    onSubmit,
}) => {
    const [name, setName] = useState('');

    useEffect(() => {
        if (!visible) return;
        setName(editing?.name || '');
    }, [visible, editing]);

    const trimmed = name.trim();
    const isDuplicate = existing.some((item) => {
        if (editing && item.id === editing.id) return false;
        return item.name.trim().toLowerCase() === trimmed.toLowerCase();
    });

    const handleSubmit = (event: React.FormEvent) => {
        event.preventDefault();
        if (!trimmed || isDuplicate || isSaving) return;
        onSubmit(trimmed);
    };

    return (
        <CustomModal
            visible={visible}
            header={editing ? 'Change unit of measure' : 'Add unit of measure'}
            onDismiss={onDismiss}
            footer={
                <div className="modal-footer-actions">
                    <Btn
                        variant="ghost"
                        buttonText="Cancel"
                        buttonOnClick={onDismiss}
                        buttonDisabled={isSaving}
                    />
                    <Btn
                        variant="primary"
                        buttonText={isSaving ? 'Saving…' : editing ? 'Save' : 'Add'}
                        buttonOnClick={() => {
                            if (!trimmed || isDuplicate || isSaving) return;
                            onSubmit(trimmed);
                        }}
                        buttonDisabled={!trimmed || isDuplicate || isSaving}
                    />
                </div>
            }
        >
            <form id="um-catalog-form" onSubmit={handleSubmit}>
                <div className="cr-field">
                    <InputField
                        label="Name"
                        inputId="um-catalog-name"
                        value={name}
                        onChange={setName}
                        autoFocus
                        hasError={isDuplicate || !!error}
                    />
                    {isDuplicate && (
                        <p className="cr-field-error-text" role="alert">
                            A unit of measure with this name already exists.
                        </p>
                    )}
                    {error && (
                        <p className="cr-field-error-text" role="alert">
                            {error}
                        </p>
                    )}
                </div>
            </form>
        </CustomModal>
    );
};

export default UnitOfMeasureModal;
